import React from "react";
import { cx, getStatusMeta, TONE_CLASSES, type StatusDomain } from "@ghostwatch/ui";

/** Status pill: icon shape + label + colour, so it survives greyscale. */
export function StatusPill({ domain, value }: { domain: StatusDomain; value: string }) {
  const meta = getStatusMeta(domain, value);
  const { Icon } = meta;
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1.5 rounded border px-1.5 py-0.5 text-xs font-medium",
        TONE_CLASSES[meta.tone],
      )}
    >
      <Icon aria-hidden className="h-3.5 w-3.5 shrink-0" />
      {meta.label}
    </span>
  );
}

export function Panel({
  title,
  actions,
  children,
}: {
  title: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-[var(--border)] bg-[var(--panel)]">
      <header className="flex items-center justify-between gap-3 border-b border-[var(--border)] px-4 py-2.5">
        <h2 className="text-sm font-semibold tracking-wide text-[var(--text)]">{title}</h2>
        {actions}
      </header>
      {children}
    </section>
  );
}

export function Table({ head, children }: { head: string[]; children: React.ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wider text-[var(--text-dim)]">
            {head.map((h) => (
              <th key={h} scope="col" className="px-4 py-2 font-medium">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function Row({ children }: { children: React.ReactNode }) {
  return <tr className="border-t border-[var(--border)] hover:bg-[var(--panel-2)]">{children}</tr>;
}

export function Cell({ children, mono }: { children: React.ReactNode; mono?: boolean }) {
  return <td className={cx("px-4 py-2 align-middle", mono && "font-mono text-xs")}>{children}</td>;
}

export function Button({
  children,
  onClick,
  disabled,
  type = "button",
  variant = "default",
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  type?: "button" | "submit";
  variant?: "default" | "primary";
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={cx(
        "rounded border px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-40",
        variant === "primary"
          ? "border-[var(--tone-info-border)] bg-[var(--tone-info-bg)] text-[var(--tone-info)] hover:brightness-125"
          : "border-[var(--border)] bg-[var(--panel-2)] text-[var(--text)] hover:border-[var(--text-dim)]",
      )}
    >
      {children}
    </button>
  );
}

/**
 * One component for the three states a data view can be in. Notably `empty` is a
 * distinct state from `error` — "no alerts" and "we could not load alerts" mean
 * opposite things to an operator, and collapsing them into one blank panel is how
 * a monitoring tool ends up quietly reassuring.
 */
export function AsyncState({
  loading,
  error,
  empty,
  emptyText,
  children,
}: {
  loading: boolean;
  error: string | null;
  empty: boolean;
  emptyText: string;
  children: React.ReactNode;
}) {
  if (loading) return <p className="px-4 py-6 text-sm text-[var(--text-dim)]">Loading…</p>;
  if (error) {
    return (
      <p
        role="alert"
        className={cx("m-4 rounded border px-3 py-2 text-sm", TONE_CLASSES.critical)}
      >
        Could not load: {error}
      </p>
    );
  }
  if (empty) return <p className="px-4 py-6 text-sm text-[var(--text-dim)]">{emptyText}</p>;
  return <>{children}</>;
}

export function formatTime(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toISOString().replace("T", " ").slice(0, 19) + "Z";
}
